import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Button } from '@/components/atoms';
import { feedDriveService, type FeedCluster, type FeedFilters } from '@/services/feeddrive';

import styles from './FeedDrive.module.css';

const PLATFORMS = ['bluesky', 'reddit', 'gdelt', 'rss'];
const WINDOWS = [
  ['1h', '1 hora'],
  ['6h', '6 horas'],
  ['24h', '24 horas'],
  ['7d', '7 dias'],
] as const;

function pct(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function relativeTime(value: string): string {
  const seconds = Math.max(0, (Date.now() - new Date(value).getTime()) / 1000);
  if (seconds < 3600) return `${Math.max(1, Math.floor(seconds / 60))} min`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} h`;
  return `${Math.floor(seconds / 86400)} d`;
}

export function FeedDrive() {
  const [platforms, setPlatforms] = useState<string[]>([]);
  const [window, setWindow] = useState('24h');
  const [topic, setTopic] = useState('');
  const [entity, setEntity] = useState('');
  const [category, setCategory] = useState('');
  const [region, setRegion] = useState('');
  const [language, setLanguage] = useState('');
  const [minScore, setMinScore] = useState(0);
  const [selected, setSelected] = useState<FeedCluster | null>(null);

  const filters: FeedFilters = useMemo(
    () => ({
      platforms,
      window,
      topic: topic.trim() || undefined,
      entity: entity.trim() || undefined,
      category: category.trim() || undefined,
      region: region.trim() || undefined,
      language: language.trim() || undefined,
      minScore,
    }),
    [platforms, window, topic, entity, category, region, language, minScore],
  );

  const feed = useQuery({
    queryKey: ['feeddrive', 'feed', filters],
    queryFn: () => feedDriveService.getFeed(filters),
  });
  const connectors = useQuery({
    queryKey: ['feeddrive', 'connectors'],
    queryFn: feedDriveService.getConnectors,
    refetchInterval: 60_000,
  });

  function togglePlatform(platform: string) {
    setPlatforms((current) =>
      current.includes(platform)
        ? current.filter((item) => item !== platform)
        : [...current, platform],
    );
  }

  function clearFilters() {
    setPlatforms([]);
    setTopic('');
    setEntity('');
    setCategory('');
    setRegion('');
    setLanguage('');
    setMinScore(0);
  }

  return (
    <section className={styles.root}>
      <header className={styles.header}>
        <div>
          <span className={styles.eyebrow}>FeedDrive · inteligência multiplataforma</span>
          <h1>O que está ganhando força agora</h1>
          <p>
            Narrativas cruzadas entre fontes públicas, com proveniência, evidências e score
            explicável. Nenhum conteúdo é publicado automaticamente.
          </p>
        </div>
        <div className={styles.health}>
          {(connectors.data ?? []).map((connector) => (
            <span key={connector.id} title={connector.health?.message}>
              <i data-status={connector.health?.status ?? 'unknown'} />
              {connector.display_name}
            </span>
          ))}
        </div>
      </header>

      <div className={styles.workspace}>
        <aside className={styles.filters}>
          <div className={styles.filterHead}>
            <strong>Recorte editorial</strong>
            <button type="button" onClick={clearFilters}>
              limpar
            </button>
          </div>

          <fieldset>
            <legend>Plataformas</legend>
            <div className={styles.platforms}>
              {PLATFORMS.map((platform) => (
                <label key={platform}>
                  <input
                    type="checkbox"
                    checked={platforms.includes(platform)}
                    onChange={() => togglePlatform(platform)}
                  />
                  <span>{platform}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <label>
            <span>Janela</span>
            <select value={window} onChange={(event) => setWindow(event.target.value)}>
              {WINDOWS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Tópico</span>
            <input
              value={topic}
              onChange={(event) => setTopic(event.target.value)}
              placeholder="inteligência artificial"
            />
          </label>
          <label>
            <span>Entidade</span>
            <input
              value={entity}
              onChange={(event) => setEntity(event.target.value)}
              placeholder="pessoa, empresa, órgão"
            />
          </label>
          <label>
            <span>Categoria</span>
            <input
              value={category}
              onChange={(event) => setCategory(event.target.value)}
              placeholder="tecnologia"
            />
          </label>
          <div className={styles.split}>
            <label>
              <span>Região</span>
              <input
                value={region}
                onChange={(event) => setRegion(event.target.value)}
                placeholder="BR"
              />
            </label>
            <label>
              <span>Idioma</span>
              <input
                value={language}
                onChange={(event) => setLanguage(event.target.value)}
                placeholder="pt"
              />
            </label>
          </div>
          <label>
            <span>Score mínimo · {pct(minScore)}</span>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={minScore}
              onChange={(event) => setMinScore(Number(event.target.value))}
            />
          </label>
        </aside>

        <main className={styles.results}>
          <div className={styles.resultsHead}>
            <div>
              <strong>{feed.data?.items.length ?? 0} narrativas</strong>
              <span>
                {feed.data?.as_of
                  ? `atualizado ${relativeTime(feed.data.as_of)} atrás`
                  : 'aguardando dados'}
              </span>
            </div>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => feed.refetch()}
              loading={feed.isFetching}
            >
              Atualizar
            </Button>
          </div>

          {feed.isError && (
            <div className={styles.state}>
              <strong>FeedDrive indisponível</strong>
              <span>O backend preservou a falha do serviço isolado. Tente novamente.</span>
            </div>
          )}
          {!feed.isLoading && !feed.isError && !feed.data?.items.length && (
            <div className={styles.state}>
              <strong>Nenhuma narrativa neste recorte</strong>
              <span>Amplie a janela ou reduza o score mínimo.</span>
            </div>
          )}

          <div className={styles.grid}>
            {(feed.data?.items ?? []).map((item) => (
              <button
                type="button"
                key={item.cluster_id}
                className={styles.card}
                onClick={() => setSelected(item)}
              >
                <div className={styles.cardTop}>
                  <span className={styles.score}>{pct(item.trend_score)}</span>
                  <span className={styles.age}>{relativeTime(item.last_seen_at)} atrás</span>
                </div>
                <h2>{item.title}</h2>
                {item.summary && <p>{item.summary}</p>}
                <div className={styles.platformRow}>
                  {item.platforms.map((platform) => (
                    <span key={platform}>{platform}</span>
                  ))}
                </div>
                <div className={styles.metrics}>
                  <span>
                    <b>{item.signal_count}</b> sinais
                  </span>
                  <span>
                    <b>{item.velocity['1h'].toFixed(1)}</b> vel. 1h
                  </span>
                  <span>
                    <b>{item.evidence.length}</b> evidências
                  </span>
                </div>
                <div className={styles.tags}>
                  {[...item.topics, ...item.entities].slice(0, 5).map((tag) => (
                    <span key={tag}>{tag}</span>
                  ))}
                </div>
              </button>
            ))}
          </div>
        </main>
      </div>

      {selected && <ClusterDrawer cluster={selected} onClose={() => setSelected(null)} />}
    </section>
  );
}

function ClusterDrawer({ cluster, onClose }: { cluster: FeedCluster; onClose: () => void }) {
  const signals = useQuery({
    queryKey: ['feeddrive', 'cluster', cluster.cluster_id, 'signals'],
    queryFn: () => feedDriveService.getClusterSignals(cluster.cluster_id),
  });
  const components = cluster.score_explanation.components ?? {};

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <aside className={styles.drawer} onClick={(event) => event.stopPropagation()}>
        <header>
          <div>
            <span className={styles.eyebrow}>Narrativa · versão {cluster.cluster_version}</span>
            <h2>{cluster.title}</h2>
          </div>
          <button type="button" onClick={onClose}>
            fechar
          </button>
        </header>

        <div className={styles.drawerBody}>
          <section className={styles.scorePanel}>
            <div className={styles.bigScore}>{pct(cluster.trend_score)}</div>
            <div>
              <strong>Score explicável</strong>
              <span>{cluster.score_explanation.version ?? 'velocity-v1'}</span>
            </div>
          </section>
          <div className={styles.scoreParts}>
            {Object.entries(components).map(([name, value]) => (
              <div key={name}>
                <span>{name}</span>
                <i>
                  <b style={{ width: pct(value) }} />
                </i>
                <em>{pct(value)}</em>
              </div>
            ))}
          </div>

          <section>
            <h3>Evidências e o que estão dizendo</h3>
            <div className={styles.evidenceList}>
              {(signals.data ?? []).map((signal) => (
                <article key={signal.id}>
                  <div>
                    <span>{signal.platform}</span>
                    <time>{relativeTime(signal.captured_at)} atrás</time>
                  </div>
                  <p>{signal.text}</p>
                  <footer>
                    <span>similaridade {pct(signal.similarity)}</span>
                    {signal.url && (
                      <a href={signal.url} target="_blank" rel="noreferrer">
                        abrir fonte
                      </a>
                    )}
                  </footer>
                </article>
              ))}
            </div>
          </section>
        </div>
      </aside>
    </div>
  );
}
